/*
Copyright (C) 2026  Tech9IQ
This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as
published by the Free Software Foundation, either version 3 of the
License, or (at your option) any later version.
This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
GNU Affero General Public License for more details.
You should have received a copy of the GNU Affero General Public License
along with this program.  If not, see <http://www.gnu.org/licenses/>.
*/

/**
 * Browser error monitoring (Sentry), inert until configured.
 *
 * The DSN comes at runtime in /api/config ("sentry", from
 * SENTRY_FRONTEND_DSN on the portal server), not from the build, so it can
 * be turned on or off without rebuilding. With no DSN the SDK is never even
 * downloaded (it's a separate chunk, loaded on demand).
 *
 * When on: errors only (no tracing unless the server sets
 * SENTRY_TRACES_SAMPLE_RATE), no personal data, and every event goes
 * through scrubEvent (sentry-scrub.ts) first. Uncaught errors and unhandled
 * rejections are reported by Sentry's global handlers, which also covers
 * the class components (React rethrows render errors no boundary catches).
 */
import { parseSampleRate, scrubEvent } from "./sentry-scrub";

export interface BrowserMonitoringConfig {
  dsn: string;
  environment?: string;
  release?: string;
  tracesSampleRate?: number;
}

/** The settings in /api/config's "sentry", or null if monitoring is off. */
export const monitoringConfigFrom = (
  value: unknown,
): BrowserMonitoringConfig | null => {
  const sentry = value as Partial<BrowserMonitoringConfig> | null | undefined;
  if (!sentry || typeof sentry.dsn !== "string" || !sentry.dsn.trim()) {
    return null;
  }
  return {
    dsn: sentry.dsn.trim(),
    environment:
      typeof sentry.environment === "string" ? sentry.environment : undefined,
    release: typeof sentry.release === "string" ? sentry.release : undefined,
    tracesSampleRate: parseSampleRate(
      sentry.tracesSampleRate === undefined
        ? undefined
        : String(sentry.tracesSampleRate),
    ),
  };
};

/** Sentry's options for a config: errors only, no personal data, every event scrubbed. */
export const sentryOptions = (config: BrowserMonitoringConfig) => ({
  dsn: config.dsn,
  environment: config.environment,
  release: config.release,
  tracesSampleRate: config.tracesSampleRate ?? 0,
  sendDefaultPii: false,
  maxBreadcrumbs: 50,
  beforeSend: <T>(event: T) => scrubEvent(event),
  beforeSendTransaction: <T>(event: T) => scrubEvent(event),
});

let started = false;

/**
 * Starts Sentry if the settings have a DSN. Never throws; call it without
 * awaiting. Safe to call again (the config is reloaded on some pages).
 */
export const startMonitoring = async (value: unknown): Promise<boolean> => {
  if (started) return true;
  try {
    const config = monitoringConfigFrom(value);
    if (!config) return false;
    started = true;
    const Sentry = await import(
      /* webpackChunkName: "sentry" */ "@sentry/react"
    );
    Sentry.init(sentryOptions(config));
    return true;
  } catch {
    started = false;
    return false;
  }
};
